import { Button, PasswordField, Spinner, useTranslation } from '@hbs-us/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError, apiFetchJson } from '../../lib/apiFetch.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useEmployeesQuery } from '../employees/useEmployeesQuery.js';
import type { UserInviteInput, UserListItemDto, UserRole } from './contractsMirror.js';
import { USER_ROLES } from './searchParams.js';
import { usersQueryKey, useUserPatchMutation, useUsersQuery } from './useUsersQuery.js';

/**
 * Create / edit user form (legacy `AddUserPage` / `EditUserPage`), redesigned to the locked
 * brand as an archetype E grouped-section form and WIRED to the api-host writes (Plan 22-24).
 *
 * CREATE (invite): POST /api/v1/users — name / email / role / password + an optional employee
 * link (a picker over the active employees). The Employee-role-must-link rule is surfaced as
 * client validation and enforced by the server.
 *
 * EDIT: PATCH /api/v1/users/:id — name / email / employee re-link (all additive) + an admin
 * password reset behind a confirm dialog. The role + per-permission overrides remain on the
 * Permissions matrix (D-03); the active flag toggles from the directory's deactivate dialog.
 * NO tenantId is ever sent (the server scopes to the principal, SEC-11). Every color is a locked
 * token (BRAND-06); each CTA carries a SPECIFIC t()-keyed label (A11Y-01).
 */

const SECTION = 'rounded-md border border-border bg-card p-6';
/**
 * The api worker's own minimum, from `validatePassword`
 * (qelm-backend/workers/api/src/lib/validation.ts). Both writes on this screen —
 * POST /api/v1/users and the PATCH /api/v1/users/:id reset — run it, so both boxes
 * print the rule instead of waiting to reject a password already chosen.
 */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Block, and 6px to the control below it, so a hand-rolled field lands on the same
 * horizontal line as a kit `Field` sitting beside it in the grid. An inline label plus
 * `mt-1` measures 2px shorter and left Role and Starting password visibly out of step.
 */
const FIELD_LABEL = 'block font-medium text-foreground text-sm';
const INPUT =
  'mt-1.5 h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export interface UserFormProps {
  mode: 'create' | 'edit';
}

const editRouteApi = getRouteApi('/users/$id');

/** Active, non-archived employees available to link (the picker options). */
function useActiveEmployeeOptions() {
  const query = useEmployeesQuery({});
  const employees = (query.data?.employees ?? []).filter((e) => e.active === 1 && !e.archivedAt);
  return employees.map((e) => ({ id: e.id, name: e.name }));
}

function CreateUserForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const employeeOptions = useActiveEmployeeOptions();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('Employee');
  const [password, setPassword] = useState('');
  const [employeeId, setEmployeeId] = useState<string>('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const mustLink = role === 'Employee' && employeeId === '';

  const mutation = useMutation<{ ok: boolean }, Error, UserInviteInput>({
    mutationFn: (input) =>
      apiFetchJson<{ ok: boolean }>('/api/v1/users', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersQueryKey() });
      void navigate({ to: '/users' });
    },
  });

  return (
    <form
      className="max-w-2xl space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        setAttemptedSubmit(true);
        if (mustLink) return;
        mutation.mutate({
          name,
          email,
          role,
          password,
          employeeId: employeeId ? Number(employeeId) : null,
        });
      }}
    >
      <section className={SECTION}>
        <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
          {t('users:form.userHeading')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="user-name" className={FIELD_LABEL}>
              {t('users:form.name')}
            </label>
            <input
              id="user-name"
              className={INPUT}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="user-email" className={FIELD_LABEL}>
              {t('users:form.email')}
            </label>
            <input
              id="user-email"
              type="email"
              className={INPUT}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="user-role" className={FIELD_LABEL}>
              {t('users:form.role')}
            </label>
            <select
              id="user-role"
              className={INPUT}
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {USER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`users:roles.${r}`)}
                </option>
              ))}
            </select>
          </div>
          <PasswordField
            className="bg-background"
            label={t('users:form.password')}
            placeholder={t('common:password.newPlaceholder')}
            helperText={`${t('common:password.minLength', { count: MIN_PASSWORD_LENGTH })} ${t('users:form.passwordHint')}`}
            showLabel={t('common:password.show')}
            hideLabel={t('common:password.hide')}
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div>
            <label htmlFor="user-employee" className={FIELD_LABEL}>
              {t('users:form.linkedEmployee')}
            </label>
            <select
              id="user-employee"
              className={INPUT}
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">{t('users:form.linkedEmployeeNone')}</option>
              {employeeOptions.map((emp) => (
                <option key={emp.id} value={String(emp.id)}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Two elements, not one with a role toggled on: screen readers do not reliably announce
            a role added to a node that was already on the page, but they do announce an alert
            that is inserted. The keys stop React reusing one <p> for both. */}
        {mustLink && !attemptedSubmit ? (
          <p key="must-link-hint" className="mt-3 text-muted-foreground text-sm">
            {t('users:form.mustLinkEmployee')}
          </p>
        ) : null}
        {mustLink && attemptedSubmit ? (
          <p key="must-link-error" role="alert" className="mt-3 text-destructive text-sm">
            {t('users:form.mustLinkEmployee')}
          </p>
        ) : null}
      </section>

      {mutation.isError ? (
        <p role="alert" aria-live="assertive" className="text-destructive text-sm">
          {t('users:form.inviteError')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {t('users:form.submitCreate')}
        </Button>
        <Button asChild variant="secondary">
          <Link to="/users">{t('users:form.cancel')}</Link>
        </Button>
      </div>
    </form>
  );
}

function EditUserForm({ user }: { user: UserListItemDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const employeeOptions = useActiveEmployeeOptions();
  const patchMutation = useUserPatchMutation();
  const passwordMutation = useUserPatchMutation();

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [employeeId, setEmployeeId] = useState<string>(
    user.employeeId != null ? String(user.employeeId) : '',
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmingReset, setConfirmingReset] = useState(false);

  const saveError =
    patchMutation.error instanceof ApiError
      ? t(`users:form.editErrors.${patchMutation.error.message}`, {
          defaultValue: t('users:form.editErrors.generic'),
        })
      : null;

  return (
    <div className="max-w-2xl space-y-4">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          patchMutation.mutate(
            {
              userId: user.id,
              name,
              email,
              employeeId: employeeId ? Number(employeeId) : null,
            },
            { onSuccess: () => void navigate({ to: '/users' }) },
          );
        }}
      >
        <section className={SECTION}>
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('users:form.userHeading')}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="edit-user-name" className={FIELD_LABEL}>
                {t('users:form.name')}
              </label>
              <input
                id="edit-user-name"
                className={INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="edit-user-email" className={FIELD_LABEL}>
                {t('users:form.email')}
              </label>
              <input
                id="edit-user-email"
                type="email"
                className={INPUT}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="edit-user-employee" className={FIELD_LABEL}>
                {t('users:form.linkedEmployee')}
              </label>
              <select
                id="edit-user-employee"
                className={INPUT}
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">{t('users:form.linkedEmployeeNone')}</option>
                {employeeOptions.map((emp) => (
                  <option key={emp.id} value={String(emp.id)}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {saveError ? (
          <p role="alert" aria-live="assertive" className="text-destructive text-sm">
            {saveError}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={patchMutation.isPending}>
            {t('users:form.submitEdit')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/users/permissions">{t('users:form.goToPermissions')}</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/users">{t('users:form.cancel')}</Link>
          </Button>
        </div>
      </form>

      <section className={SECTION}>
        <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
          {t('users:form.passwordResetHeading')}
        </h2>
        <PasswordField
          className="bg-background"
          label={t('users:form.newPassword')}
          placeholder={t('common:password.newPlaceholder')}
          helperText={t('common:password.minLength', { count: MIN_PASSWORD_LENGTH })}
          showLabel={t('common:password.show')}
          hideLabel={t('common:password.hide')}
          value={newPassword}
          autoComplete="new-password"
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <div className="mt-4">
          <Button
            type="button"
            variant="secondary"
            disabled={newPassword.length === 0 || passwordMutation.isPending}
            onClick={() => setConfirmingReset(true)}
          >
            {t('users:form.resetPassword')}
          </Button>
        </div>
        {passwordMutation.isSuccess ? (
          <p role="status" className="mt-3 text-muted-foreground text-sm">
            {t('users:form.passwordResetDone')}
          </p>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirmingReset}
        heading={t('users:form.resetPasswordConfirmHeading')}
        body={t('users:form.resetPasswordConfirmBody', { name: user.name })}
        confirmLabel={t('users:form.resetPassword')}
        cancelLabel={t('users:form.cancel')}
        confirmPending={passwordMutation.isPending}
        onCancel={() => setConfirmingReset(false)}
        onConfirm={() => {
          passwordMutation.mutate(
            { userId: user.id, newPassword },
            {
              onSuccess: () => {
                setNewPassword('');
                setConfirmingReset(false);
              },
            },
          );
        }}
      />
    </div>
  );
}

function EditUserLoader() {
  const { t } = useTranslation();
  const { id } = editRouteApi.useParams();
  const userId = Number(id);
  const query = useUsersQuery();

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const user = (query.data?.users ?? []).find((u) => u.id === userId);
  if (query.isError || !user) {
    return (
      <section role="alert" aria-live="assertive" className={SECTION}>
        <p className="font-medium text-foreground text-sm">{t('users:form.editNotFound')}</p>
      </section>
    );
  }

  return <EditUserForm user={user} />;
}

export function UserForm({ mode }: UserFormProps) {
  const { t } = useTranslation();
  const headingKey = mode === 'create' ? 'users:form.createHeading' : 'users:form.editHeading';

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display font-semibold text-3xl text-foreground">{t(headingKey)}</h1>
      </header>
      {mode === 'create' ? <CreateUserForm /> : <EditUserLoader />}
    </div>
  );
}
